import { useEffect, useState } from "react";
import type { BridgeBillingNotice, BridgeBillingHistoryResponse } from "@tuxx/orbit-bridge";
import { useOrbit } from "../provider";

export function OrbitBillingSettingsPanel() {
  const orbit = useOrbit();
  const [history, setHistory] = useState<BridgeBillingNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await orbit.billing.history();
        if (!cancelled) setHistory((response as BridgeBillingHistoryResponse).notices ?? []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Billing history could not load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orbit]);

  const openBillingPortal = async () => {
    setOpeningPortal(true);
    setError(null);
    try {
      const response = await orbit.billing.portal({ return_url: window.location.href });
      window.open(response.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not open Stripe billing portal");
    } finally {
      setOpeningPortal(false);
    }
  };

  return (
    <div className="orbit-billing-settings">
      <section className="orbit-billing-settings__hero">
        <div>
          <h4>Payment settings</h4>
          <p>Manage card details, billing email, and Stripe invoices without leaving your dashboard.</p>
        </div>
        <button type="button" className="orbit-button" onClick={openBillingPortal} disabled={openingPortal}>
          {openingPortal ? "Opening..." : "Manage payments"}
        </button>
      </section>

      {error ? <div className="orbit-billing orbit-billing--error">{error}</div> : null}

      <section className="orbit-billing-settings__history">
        <div className="orbit-billing-settings__section-head">
          <h4>Billing history</h4>
          <span>{loading ? "Loading..." : `${history.length} items`}</span>
        </div>

        {!loading && history.length === 0 ? (
          <div className="orbit-billing--empty">No Stripe invoices found for this account yet.</div>
        ) : null}

        {history.length > 0 ? (
          <div className="orbit-billing-settings__list">
            {history.slice(0, 8).map((notice) => (
              <a
                key={notice.id}
                className="orbit-billing-settings__item"
                href={notice.action_url || undefined}
                target="_blank"
                rel="noreferrer"
              >
                <span>
                  <strong>{notice.title}</strong>
                  <small>{notice.due_date ? `Due ${formatDate(notice.due_date)}` : formatDate(notice.created_at)}</small>
                </span>
                <span className={`orbit-billing-settings__status orbit-billing-settings__status--${notice.status}`}>
                  {notice.status}
                </span>
              </a>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}
